import {
  Component,
  OnInit,
  OnDestroy,
  NgZone,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, interval, BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { CommonModule } from '@angular/common';
import { LanguageService } from '@services/language/language.service';
import { getFormattedTime, TimeData } from '@shared/utils/date-utils';

@Component({
    selector: 'app-clock',
    imports: [CommonModule],
    templateUrl: './clock.component.html',
    styleUrl: './clock.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClockComponent implements OnInit, OnDestroy {
  private subscription?: Subscription;
  private destroy$ = new Subject<void>();
  private monthsShort: Record<string, string> = {};

  private timeSubject = new BehaviorSubject<TimeData>(this.getInitialTime());
  time$ = this.timeSubject.asObservable();

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private languageService: LanguageService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadTranslations();

    this.languageService.forceChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language: string) => {
        this.loadTranslations();
      });

    this.ngZone.runOutsideAngular(() => {
      this.subscription = interval(1000).subscribe(() => {
        const newTime = getFormattedTime(this.monthsShort);
        this.timeSubject.next(newTime);
      });
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.timeSubject.complete();
  }

    private loadTranslations(): void {
      this.languageService.getSharedDictionary('months_short').pipe(
        takeUntil(this.destroy$)
      ).subscribe((translations: any) => {
        this.monthsShort = translations || {};
        this.timeSubject.next(getFormattedTime(this.monthsShort));
      });
    }

  private getInitialTime(): TimeData {
    const now = new Date();
    const day = now.getUTCDate().toString().padStart(2, '0');
    const monthIndex = now.getUTCMonth();
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const year = now.getUTCFullYear();
    const hours = now.getUTCHours().toString().padStart(2, '0');
    const minutes = now.getUTCMinutes().toString().padStart(2, '0');

    return {
      dateFormatted: `${day} ${monthNames[monthIndex]} ${year}`,
      timeFormatted: `${hours}:${minutes}`,
    };
  }
}
